import { StockItem } from "@/types/stock-item";

export interface StockMovementValidationResult {
  canProceed: boolean;
  validItems: StockItem[];
  invalidItems: StockItem[];
  warnings: string[];
}

/**
 * Validates stock items before starting a stock movement
 * Rules:
 * - Cannot start movement with inactive items
 */
export function validateStockMovementStart(
  items: StockItem[]
): StockMovementValidationResult {
  const validItems: StockItem[] = [];
  const invalidItems: StockItem[] = [];
  const warnings: string[] = [];

  items.forEach((item) => {
    // Check if item is inactive
    if (item.inactive) {
      invalidItems.push(item);
    } else {
      validItems.push(item);
    }
  });

  // Can proceed if at least one valid item
  const canProceed = validItems.length > 0;

  // Add warnings
  if (invalidItems.length > 0) {
    warnings.push(
      `${invalidItems.length} inactive item${invalidItems.length > 1 ? "s" : ""} will be excluded`
    );
  }

  return {
    canProceed,
    validItems,
    invalidItems,
    warnings,
  };
}

/**
 * Prepares stock items for pre-population in stock movement
 */
export interface PreparedStockMovementItem {
  stockCode: string;
  stockDescription: string;
  location: string;
  warehouseCode: string;
  locationCode: string;
  unitCost: number;
  hasNoCost: boolean;
  currentQuantity: number;
  unitOfMeasure: string;
}

export function prepareStockMovementItems(
  items: StockItem[]
): PreparedStockMovementItem[] {
  return items.map((item) => ({
    stockCode: item.code,
    stockDescription: item.description,
    location: `${item.warehouseCode} - ${item.locationCode}`,
    warehouseCode: item.warehouseCode,
    locationCode: item.locationCode,
    unitCost: item.averageCost || 0,
    hasNoCost: !item.averageCost || item.averageCost === 0,
    currentQuantity: item.physicalQuantity,
    unitOfMeasure: item.unitOfMeasure,
  }));
}
